import {useForm, zodResolver} from '@mantine/form';
import {useDisclosure} from '@mantine/hooks';
import type {AppError} from '@packages/utils';
import {useEffect, useState} from 'react';
import {DEFAULT_NICKNAME} from '../../../../core/entities/community/constants';
import {PostCommunityCommentFormInput} from '../../../../core/entities/community/schema';
import {usePostCommunityComment} from '../../../../core/repositories/communityRepository';
import {queryClient} from '../../../../lib/react-query';
import {
  showErrorSnackBar,
  showSuccessSnackBar,
} from '../../../../shared/components';
import {ConfirmCommentFormModal} from './Presenter/ConfirmCommentFormModal';

type Props = {
  communityNickname: string;
  topicId: number;
  seqNumberToReply: number;
  setSeqNumberToReply: (seqNumber: number) => void;
  scrollToBottomItem: () => void;
};

type CastedError = {message: AppError};

export const PostCommentForm: React.FC<Props> = ({
  communityNickname,
  topicId,
  seqNumberToReply,
  setSeqNumberToReply,
  scrollToBottomItem,
}) => {
  const [opened, {open, close}] = useDisclosure(false);
  const [isPostLoading, setIsPostLoading] = useState(false);

  const form = useForm<PostCommunityCommentFormInput>({
    initialValues: {
      nickname: communityNickname ?? '',
      body: '',
    },
    validate: zodResolver(PostCommunityCommentFormInput),
  });

  const onSubmit = () => {
    open();
  };

  const onError = () => {
    form.setDirty({
      nickname: true,
      body: true,
    });
  };

  const postComment = usePostCommunityComment({
    async onSuccessFnc() {
      showSuccessSnackBar({title: '送信しました', message: 'done'});
      await queryClient.invalidateQueries({
        queryKey: ['getCommunityCommentList'],
      });
      form.reset();
      setTimeout(() => {
        scrollToBottomItem();
      }, 300); // リストの再取得が終わるのを待つ
    },
    onMutateFnc() {
      setIsPostLoading(true);
      if (form.values.nickname === '') {
        form.values.nickname = DEFAULT_NICKNAME;
      }
    },
    onErrorFnc(error: unknown) {
      const castedError = error as CastedError;
      showErrorSnackBar({
        isNetworkError: castedError.message === 'NetworkError',
      });
    },
    onSettledFnc() {
      setIsPostLoading(false);
      close();
    },
  });

  const postCommentFunc = async () => {
    postComment.mutate({
      topicId,
      nickname: form.values.nickname,
      body: form.values.body,
    });
  };

  useEffect(() => {
    if (seqNumberToReply !== 0) {
      form.setValues({
        ...form.values,
        body: `>>${seqNumberToReply}\n${form.values.body}`,
      });
      setSeqNumberToReply(0);
    }
  }, [seqNumberToReply]);

  return (
    <div className="bg-white p-16 shadow-modal rounded-12 mt-16">
      <p className="text-base font-bold text-black-1 leading">
        知りたいことを投稿してみましょう
      </p>
      <form onSubmit={form.onSubmit(onSubmit, onError)} className="mt-12">
        <div>
          <label className="text-base font-bold text-black-1 leading block mb-8">
            ニックネーム
          </label>
          <input
            id="nickname"
            {...form.getInputProps('nickname')}
            placeholder="ニックネーム"
            className="border-[1px] border-line border-collapse p-8 w-full rounded-[8px] text-base"
            autoComplete="off"
            type="text"
          />
          {form.isDirty('nickname') && form.errors.nickname && (
            <p className="text-notification-1 text-sm leading mt-8">
              {form.errors.nickname}
            </p>
          )}
          <div className="mt-8">
            <p className="text-base text-black-1 leading">
              * #任意の言葉 で固定の名前が使えます。
            </p>
            <p className="text-base text-black-1 leading">
              * 空白の場合「{DEFAULT_NICKNAME}」として投稿されます。
            </p>
          </div>
        </div>
        <div className="pt-16">
          <div className="flex flex-wrap mb-8">
            <label className="text-base font-bold text-black-1 leading block">
              投稿内容
            </label>
            <span className="block bg-notification-1 text-white text-xs py-[2px] px-4 rounded-full leading ml-4">
              必須
            </span>
          </div>
          <textarea
            id="body"
            {...form.getInputProps('body')}
            placeholder={''}
            rows={8}
            className="border-[1px] border-line border-collapse p-8 w-full rounded-[8px] text-base"
          />
          {form.isDirty('body') && form.errors.body && (
            <p className="text-notification-1 text-sm leading mt-8">
              {form.errors.body}
            </p>
          )}
        </div>
        <div className="pt-16 flex justify-end">
          <button
            type="submit"
            className={`text-base font-bold bg-black-3  ${Object.keys(form.errors).length > 0 ? 'text-black-1' : 'text-white'} py-8 px-12 rounded-full ${Object.keys(form.errors).length > 0 ? 'bg-disabled-1' : 'bg-primary-1'}`}
            disabled={Object.keys(form.errors).length > 0}
          >
            確認画面へ
          </button>
        </div>
      </form>
      <ConfirmCommentFormModal
        nickname={form.values.nickname}
        body={form.values.body}
        opened={opened}
        close={close}
        postCommentFunc={postCommentFunc}
        isPostLoading={isPostLoading}
      />
    </div>
  );
};
